import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useTelegram } from './TelegramContext';
import { identifyUser, resetUser } from '../lib/observability';
import { generatePublicId, isValidPublicId, sourceFromAuthMethod } from '../lib/userId';
import { wipeLocalCache } from '../lib/redact';
import {
  isFirebaseEnabled,
  firebaseSignInWithEmail,
  firebaseCreateUserWithEmail,
  firebaseSignInWithGoogle,
  firebaseSignInWithApple,
  firebaseSignOut,
  getFirebaseIdToken,
} from '../lib/firebase';
import { getProfile as apiGetProfile, updateProfile as apiUpdateProfile, deleteAccount as apiDeleteAccount } from '../lib/workoutApi';

// ── Cross-app: mock token for environments without Firebase ────────────────────
const IS_MOCK_MODE =
  !import.meta.env.VITE_FIREBASE_API_KEY ||
  import.meta.env.VITE_FIREBASE_API_KEY === 'placeholder' ||
  String(import.meta.env.VITE_FIREBASE_API_KEY).startsWith('YOUR_');
const MOCK_ID_TOKEN = 'mock-id-token-omnisio-workout-dev';

const AuthContext = createContext(null);

const STORAGE_KEY = 'omnisio_workout_auth';
// Holds a generated public_id that failed to sync to Supabase (offline).
// Retried on next mount.
const PENDING_PUBLIC_ID_KEY = 'omnisio_workout_pending_public_id';
// Tracks which user the cached per-user state belongs to, so a different
// user signing in can be detected and the previous user's cache wiped.
// IMPORTANT (#11): this key is INTENTIONALLY NOT cleared on logout. It is the
// breadcrumb that lets the NEXT login detect "this is a different user than the
// one whose cache may still be on disk" and purge before hydrating. The prior
// fix wiped it on logout, which made the user-change detector dead on arrival
// (prevUid was always null → the purge branch never ran).
const ACTIVE_UID_KEY = 'omnisio_workout_active_uid';

// Resolve a STABLE per-user identity. Firebase uid is preferred (it survives
// logout/login and is identical across sessions for the same account); the rest
// are fallbacks for Telegram / demo / legacy Supabase paths.
function deriveUid(profile) {
  if (!profile) return null;
  const uid =
    profile.firebase_uid || profile.uid || profile.auth_id ||
    profile.public_id || profile._id || profile.id ||
    profile.telegram_id || profile.email || null;
  return uid ? String(uid) : null;
}

// User-scoped cached state that MUST be cleared between accounts (privacy / #11).
// App-level prefs (language, sound, haptics) are intentionally NOT in this list.
// NOTE: active_uid is deliberately excluded — see ACTIVE_UID_KEY comment above.
const USER_SCOPED_KEYS = [
  'omnisio_workout_auth',
  'omnisio_workout_energy',
  'omnisio_workout_companion',
  'omnisio_workout_dragon',
  'omnisio_workout_streak',
  'omnisio_workout_profile',
  'omnisio_workout_weekly',
  'omnisio_workout_pending_public_id',
];

// Prefix for the USER-SCOPED onboarding-completed flag (#13). The legacy global
// flag `omnisio_workout_onboarding_done` is migrated/ignored — onboarding state
// is now keyed by uid so a new account always sees onboarding and a returning
// account skips it, regardless of who logged in before.
const ONBOARDING_DONE_PREFIX = 'omnisio_workout_onboarding_done_';
export function onboardingKeyForUid(uid) {
  return uid ? `${ONBOARDING_DONE_PREFIX}${uid}` : null;
}

/**
 * Clear every user-specific localStorage key. Removes the explicit
 * USER_SCOPED_KEYS plus any stray `omnisio_workout_*` / `omnisio_xapp_*`
 * cache key, while preserving app-level preference keys.
 */
const PRESERVED_PREF_KEYS = new Set([
  'omnisio_workout_language',
  'omnisio_workout_lang',
  'omnisio_workout_sound',
  'omnisio_workout_haptics',
  'omnisio_workout_prefs',
  // v1 launch: Pro is free for everyone via this global flag (set in main.jsx).
  // It is NOT per-user private data — preserve it across logout/account-switch
  // so Pro features (Coach, voice) don't lock after switching without a reload.
  // When IAP ships, this becomes per-user and moves out of the preserved set.
  'omnisio_workout_subscription_active',
  'i18nextLng',
]);

// Keys that survive a user-scope wipe because they are managed deliberately:
//  - active_uid: breadcrumb for next-login user-change detection.
//  - onboarding_done_<uid>: per-user onboarding flags; wiping all of them would
//    force a returning user back through onboarding. Each user's own flag stays.
function isDeliberatelyManagedKey(key) {
  return key === ACTIVE_UID_KEY || key.startsWith(ONBOARDING_DONE_PREFIX);
}

/**
 * Clear every user-specific localStorage key. Removes the explicit
 * USER_SCOPED_KEYS plus any stray `omnisio_workout_*` / `omnisio_xapp_*`
 * cache key, while preserving app-level preference keys, the active-uid
 * breadcrumb, and per-user onboarding flags.
 */
function clearUserScopedCache() {
  // Explicit keys first.
  for (const key of USER_SCOPED_KEYS) {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }
  // Also drop the legacy GLOBAL onboarding flag so it can never leak onboarding
  // state across accounts. Per-user flags (onboarding_done_<uid>) are kept.
  try { localStorage.removeItem('omnisio_workout_onboarding_done'); } catch { /* ignore */ }
  // Sweep any remaining user-data keys (biometric/companion/cache) but keep
  // prefs and the deliberately-managed keys.
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (PRESERVED_PREF_KEYS.has(key)) continue;
      if (isDeliberatelyManagedKey(key)) continue;
      if (key.startsWith('omnisio_workout_') || key.startsWith('omnisio_xapp_')) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    }
  } catch { /* ignore */ }
}

// Purge the PREVIOUS user's cached per-user data BEFORE the new user hydrates.
// Unlike clearUserScopedCache (logout), this is called on login when a uid
// mismatch is detected. It removes cached profile/energy/companion/streak/weekly
// + cross-app snapshots so EnergyContext and others re-init clean for the new
// account. App prefs, active_uid and per-user onboarding flags are preserved.
function purgePreviousUserData() {
  const PREV_USER_KEYS = [
    'omnisio_workout_auth',
    'omnisio_workout_energy',
    'omnisio_workout_companion',
    'omnisio_workout_dragon',
    'omnisio_workout_streak',
    'omnisio_workout_weekly',
    'omnisio_workout_profile',
    'omnisio_workout_pending_public_id',
    'omnisio_workout_onboarding_done', // legacy global flag
    'omnisio_xapp_snapshot',
    'omnisio_xapp_baseline',
    'omnisio_xapp_recommendation',
  ];
  for (const key of PREV_USER_KEYS) {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (PRESERVED_PREF_KEYS.has(key)) continue;
      if (isDeliberatelyManagedKey(key)) continue;
      if (key.startsWith('omnisio_workout_') || key.startsWith('omnisio_xapp_')) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    }
  } catch { /* ignore */ }
}

const DEFAULT_PROFILE = {
  tokens: 0,
  energy: 100,
  level: 1,
  xp: 0,
  totem: null,
  syndicate_id: null,
  streak: 0,
  total_workouts: 0,
};

// Helpers for localStorage persistence
function loadAuthState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Basic validity check
    if (parsed && typeof parsed.isAuthenticated === 'boolean') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function saveAuthState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.error('[OMNISIO] Failed to save auth state:', err);
  }
}

function clearAuthState() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.error('[OMNISIO] Failed to clear auth state:', err);
  }
}

/**
 * Ensure a profile has a public_id. If missing:
 *  1. Generate one using the correct source (workout vs longevity).
 *  2. Attempt to persist it to Supabase immediately.
 *  3. If Supabase is unavailable, stash the ID in localStorage under
 *     PENDING_PUBLIC_ID_KEY so we can retry on next mount.
 *
 * @param {object} profileData  - The profile object from Supabase (may lack public_id)
 * @param {string} currentAuthMethod  - Value of authMethod state
 * @param {function} updateProfileFn  - updateProfile callback (from context)
 * @returns {Promise<object>}  The profile with public_id guaranteed to be set
 */
async function ensurePublicId(profileData, currentAuthMethod, updateProfileFn) {
  // If the profile already has a valid public_id, nothing to do.
  if (profileData?.public_id && isValidPublicId(profileData.public_id)) {
    return profileData;
  }

  // Check if we have a pending ID from a previous offline session
  let pendingId = null;
  try {
    pendingId = localStorage.getItem(PENDING_PUBLIC_ID_KEY);
  } catch { /* ignore */ }

  // Use the pending ID if still valid, otherwise generate a fresh one
  const source = sourceFromAuthMethod(currentAuthMethod);
  const newPublicId = (pendingId && isValidPublicId(pendingId))
    ? pendingId
    : generatePublicId(source);

  console.log('[OMNISIO] Assigning public_id:', newPublicId, 'source:', source);

  // Attempt to persist to Supabase
  const updated = await updateProfileFn({ public_id: newPublicId });

  if (updated) {
    // Success — clear any pending ID stash
    try { localStorage.removeItem(PENDING_PUBLIC_ID_KEY); } catch { /* ignore */ }
    return updated;
  } else {
    // Supabase unavailable — stash for retry on next mount
    try {
      localStorage.setItem(PENDING_PUBLIC_ID_KEY, newPublicId);
    } catch { /* ignore */ }
    // Return an augmented local copy so callers have the ID immediately
    return { ...profileData, public_id: newPublicId };
  }
}

export function AuthProvider({ children }) {
  const { user: tgUser, isTelegram, isReady: tgReady } = useTelegram();

  // Initialize state from localStorage synchronously
  const savedAuth = loadAuthState();
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(savedAuth?.profile || null);
  // Cross-app: cached ID token for Longevity API calls
  const cachedIdTokenRef = useRef(null);
  const tokenFetchedAtRef = useRef(null);
  const TOKEN_REFRESH_INTERVAL_MS = 55 * 60 * 1000; // 55 minutes
  const [isAuthenticated, setIsAuthenticated] = useState(savedAuth?.isAuthenticated || false);
  const [authMethod, setAuthMethod] = useState(savedAuth?.authMethod || null);
  // Loading must stay TRUE until auth + profile are definitively resolved, even
  // when a cached session exists (#13). Rendering the app off a stale cache
  // before the fresh profile arrives is exactly what made the landing screen
  // flicker between onboarding and home. We flip it to false only once the
  // initial resolution path (Telegram / Firebase restore / no-session) settles.
  const [loading, setLoading] = useState(true);
  // True once the very first auth resolution pass has completed. Routing in
  // App.jsx waits on `loading === false` which is driven by this.
  const authResolvedRef = useRef(false);
  const [error, setError] = useState(null);

  // Persist auth state whenever it changes
  useEffect(() => {
    if (isAuthenticated && profile) {
      saveAuthState({
        isAuthenticated,
        authMethod,
        profile,
        updatedAt: new Date().toISOString(),
      });
    }
  }, [isAuthenticated, authMethod, profile]);

  // ─── #11 SESSION/CACHE ISOLATION — detect user change on login ───────────────
  // A STABLE per-user identity (uid, firebase uid preferred) is derived from the
  // active profile. It is compared against `active_uid` persisted on the LAST
  // login — a breadcrumb that deliberately SURVIVES logout (see ACTIVE_UID_KEY).
  // When the uids differ (account switch), the previous user's cached per-user
  // state is purged BEFORE any child (EnergyContext) can hydrate from it, then
  // the fresh server profile is fetched so the displayed name/data is the
  // current user's — never a leftover cache. We track the handled uid in a ref
  // so this runs exactly once per real switch (not on every profile mutation).
  const handledUidRef = useRef(null);
  useEffect(() => {
    if (!isAuthenticated || !profile) return;
    const uid = deriveUid(profile);
    if (!uid) return;
    if (handledUidRef.current === uid) return; // already reconciled this user

    let prevUid = null;
    try { prevUid = localStorage.getItem(ACTIVE_UID_KEY); } catch { /* ignore */ }

    if (prevUid && prevUid !== uid) {
      // Account switch detected. Purge the previous user's cached data FIRST so
      // EnergyContext (and anything else reading localStorage) re-initializes
      // clean, then re-fetch THIS user's profile from the backend as the single
      // source of truth.
      purgePreviousUserData();
      refreshProfile();
    }

    try { localStorage.setItem(ACTIVE_UID_KEY, uid); } catch { /* ignore */ }
    handledUidRef.current = uid;
    // refreshProfile intentionally omitted from deps: it depends on `profile`,
    // and re-running on its identity change would loop. handledUidRef guards
    // against re-entry; the uid value covers genuine user change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, profile?.firebase_uid, profile?.uid, profile?.auth_id, profile?.public_id, profile?._id, profile?.id, profile?.telegram_id, profile?.email]);

  // Fetch or create profile from Supabase
  const fetchProfile = useCallback(async (telegramId) => {
    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('telegram_id', telegramId)
        .single();

      if (fetchError && fetchError.code === 'PGRST116') {
        // Profile not found, create one
        const newProfile = {
          telegram_id: telegramId,
          username: tgUser?.username || '',
          first_name: tgUser?.firstName || '',
          last_name: tgUser?.lastName || '',
          photo_url: tgUser?.photoUrl || null,
          language_code: tgUser?.languageCode || 'en',
          is_premium: tgUser?.isPremium || false,
          ...DEFAULT_PROFILE,
          created_at: new Date().toISOString(),
        };

        const { data: created, error: createError } = await supabase
          .from('profiles')
          .insert(newProfile)
          .select()
          .single();

        if (createError) throw createError;
        setProfile(created);
        return created;
      }

      if (fetchError) throw fetchError;
      setProfile(data);
      return data;
    } catch (err) {
      console.error('[OMNISIO] Profile fetch error:', err);
      setError(err.message);
      return null;
    }
  }, [tgUser]);

  // Update profile fields — backend (Mongo via /api) when Firebase signed-in,
  // else optimistic local update (demo / offline).
  const updateProfile = useCallback(async (updates) => {
    // Optimistic local update first (instant UI)
    setProfile((prev) => ({ ...(prev || {}), ...updates }));
    try {
      if (isFirebaseEnabled() && (await getFirebaseIdToken())) {
        const { profile: serverProfile } = await apiUpdateProfile(updates);
        setProfile(serverProfile);
        return serverProfile;
      }
    } catch (err) {
      console.warn('[OMNISIO] Profile update sync failed (kept local):', err.message);
    }
    return { ...(profile || {}), ...updates };
  }, [profile]);

  // ─── Cross-agent contract: refreshProfile ───────────────────────────────────
  // Re-fetches the CURRENT user's profile from the backend (/api/profile),
  // updates in-memory state, and persists to the saved-auth localStorage so a
  // reload shows fresh data. Called after a workout completes (another agent),
  // and on login when a different user is detected.
  // No-op (returns current profile) when Firebase isn't signed in.
  const refreshProfile = useCallback(async () => {
    try {
      if (isFirebaseEnabled() && (await getFirebaseIdToken())) {
        const { profile: serverProfile } = await apiGetProfile(authMethod || undefined);
        if (serverProfile) {
          setProfile(serverProfile);
          // Persist immediately (the effect also saves, but be explicit so the
          // saved-auth cache is fresh even before the next render cycle).
          try {
            saveAuthState({
              isAuthenticated: true,
              authMethod,
              profile: serverProfile,
              updatedAt: new Date().toISOString(),
            });
          } catch { /* ignore */ }
          return serverProfile;
        }
      }
    } catch (err) {
      console.warn('[OMNISIO] refreshProfile failed (kept current):', err.message);
    }
    return profile;
  }, [authMethod, profile]);

  // Add tokens
  const addTokens = useCallback(async (amount) => {
    if (!profile) return;
    return updateProfile({ tokens: (profile.tokens || 0) + amount });
  }, [profile, updateProfile]);

  // ─── Multi-method login (Firebase Auth + MongoDB backend) ─────────────────
  // Flow: Firebase sign-in → ID token → backend /api/profile (create/fetch
  // Mongo user). Falls back to localStorage demo mode if Firebase not enabled.
  const login = useCallback(async (method, credentials = {}) => {
    setLoading(true);
    setError(null);

    try {
      // ── Demo / mock fallback (no Firebase configured) ──────────────────
      if (!isFirebaseEnabled()) {
        const demoProfile = {
          ...DEFAULT_PROFILE,
          email: credentials.email || 'demo@omnisio.app',
          first_name: 'Athlete',
          public_id: generatePublicId(sourceFromAuthMethod(method)),
        };
        setProfile(demoProfile);
        setAuthMethod(method);
        setIsAuthenticated(true);
        return { step: 'complete', profile: demoProfile, demo: true };
      }

      let fbUser = null;

      switch (method) {
        case 'email': {
          const { email, password } = credentials;
          if (!email || !password) throw new Error('Email and password are required');
          // Sign-in, or auto-create on first use (sign-up-or-in UX)
          try {
            fbUser = await firebaseSignInWithEmail(email, password);
          } catch (e) {
            const code = e?.code || '';
            if (code.includes('user-not-found') || code.includes('invalid-credential')) {
              fbUser = await firebaseCreateUserWithEmail(email, password);
            } else {
              throw e;
            }
          }
          break;
        }
        case 'phone': {
          // Firebase phone auth needs reCAPTCHA (web) / APNs silent push (iOS) —
          // deferred. Guide user to email for now.
          setLoading(false);
          return { step: 'error', error: 'phone_unsupported',
            message: 'Phone sign-in coming soon — please use email or Google.' };
        }
        case 'google':
          fbUser = await firebaseSignInWithGoogle();
          break;
        case 'apple':
          fbUser = await firebaseSignInWithApple();
          break;
        default:
          throw new Error(`Unsupported auth method: ${method}`);
      }

      if (!fbUser) throw new Error('Sign-in failed');

      // Sync with backend — creates Mongo user + public_id on first login
      let userProfile;
      try {
        const { profile: serverProfile } = await apiGetProfile(method);
        userProfile = serverProfile;
      } catch (e) {
        // Backend unreachable — degrade gracefully to local profile
        console.warn('[OMNISIO] Backend profile sync failed, using local:', e.message);
        userProfile = {
          ...DEFAULT_PROFILE,
          email: fbUser.email,
          first_name: fbUser.displayName || 'Athlete',
          public_id: generatePublicId(sourceFromAuthMethod(method)),
        };
      }

      setProfile(userProfile);
      setAuthMethod(method);
      setIsAuthenticated(true);
      identifyUser(userProfile.public_id || userProfile._id || 'unknown', { method });

      return { step: 'complete', profile: userProfile };
    } catch (err) {
      console.error(`[OMNISIO] Login error (${method}):`, err);
      setError(err.message);
      return { step: 'error', error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Cross-app: getIdToken — chain Firebase first, then legacy fallbacks ─────
  // Priority order:
  //   1. Firebase ID token (if SDK installed + configured)
  //   2. Mock token (development / no auth configured)
  //   3. Supabase access_token (legacy, will be removed when MongoDB+Firebase live)
  const getIdToken = useCallback(async (forceRefresh = false) => {
    if (IS_MOCK_MODE) return MOCK_ID_TOKEN;
    if (
      !forceRefresh &&
      cachedIdTokenRef.current &&
      tokenFetchedAtRef.current &&
      Date.now() - tokenFetchedAtRef.current < TOKEN_REFRESH_INTERVAL_MS
    ) {
      return cachedIdTokenRef.current;
    }

    // Try Firebase first (no-op if SDK not installed or env missing)
    try {
      const { getFirebaseIdToken } = await import('../lib/firebase');
      const fbToken = await getFirebaseIdToken();
      if (fbToken) {
        cachedIdTokenRef.current = fbToken;
        tokenFetchedAtRef.current = Date.now();
        return fbToken;
      }
    } catch {
      // Firebase module load failed — fall through to legacy
    }

    // Legacy Supabase fallback (will be removed when MongoDB live)
    try {
      const { data: { session: current } } = await supabase.auth.getSession();
      const token = current?.access_token || MOCK_ID_TOKEN;
      cachedIdTokenRef.current = token;
      tokenFetchedAtRef.current = Date.now();
      return token;
    } catch {
      return MOCK_ID_TOKEN;
    }
  }, []);

  // ─── Logout ───────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try {
      if (isFirebaseEnabled()) await firebaseSignOut();
      else await supabase.auth.signOut();
    } catch (err) {
      console.error('[OMNISIO] Logout error:', err);
    } finally {
      setSession(null);
      setProfile(null);
      setIsAuthenticated(false);
      setAuthMethod(null);
      setError(null);
      clearAuthState();
      resetUser();
      // Cross-app: wipe all cached biometric data (KVKK compliance)
      cachedIdTokenRef.current = null;
      tokenFetchedAtRef.current = null;
      // Allow the next login to re-run user-change reconciliation.
      handledUidRef.current = null;
      wipeLocalCache();
      // #11 isolation: wipe ALL user-specific cache (profile/energy/companion/
      // streak/weekly/biometric) so the next user starts clean. active_uid is
      // intentionally preserved so the next login can detect an account switch.
      clearUserScopedCache();
    }
  }, []);

  // ─── Delete account (Apple 5.1.1(v) — permanent server + auth deletion) ──────
  const deleteAccount = useCallback(async () => {
    try {
      // Deletes Mongo data + Firebase auth user server-side (requires valid token).
      if (isFirebaseEnabled() && (await getFirebaseIdToken())) {
        await apiDeleteAccount();
      }
    } catch (err) {
      console.error('[OMNISIO] Delete account error:', err);
      throw err; // surface to UI so it can show an error instead of a false success
    } finally {
      // Clear all local state regardless (the server user is gone or unreachable).
      try { if (isFirebaseEnabled()) await firebaseSignOut(); } catch {}
      setSession(null);
      setProfile(null);
      setIsAuthenticated(false);
      setAuthMethod(null);
      setError(null);
      clearAuthState();
      resetUser();
      cachedIdTokenRef.current = null;
      tokenFetchedAtRef.current = null;
      handledUidRef.current = null;
      wipeLocalCache();
      // #11 isolation: wipe ALL user-specific cache on account deletion. On a
      // permanent delete we also drop the active_uid + this user's onboarding
      // flag so nothing about the removed account can resurface.
      clearUserScopedCache();
      try {
        const goneUid = localStorage.getItem(ACTIVE_UID_KEY);
        localStorage.removeItem(ACTIVE_UID_KEY);
        const okey = onboardingKeyForUid(goneUid);
        if (okey) localStorage.removeItem(okey);
      } catch { /* ignore */ }
    }
  }, []);

  // ─── Initial auth resolution (#13) ──────────────────────────────────────────
  // On mount, if a cached session was restored from localStorage, validate it
  // against the backend (Firebase) so the displayed profile is fresh, THEN flip
  // loading=false. This is what makes the post-login landing deterministic:
  // App.jsx never decides landing-vs-home off a stale cache because it waits for
  // loading to clear here. Non-Telegram, non-cached cases are resolved by the
  // Telegram effect below (which always settles loading once tgReady).
  useEffect(() => {
    if (authResolvedRef.current) return;
    // Only the cached-session branch needs async validation here. Everything
    // else (Telegram login, fresh login, no session) is resolved elsewhere.
    if (!savedAuth?.isAuthenticated) return;

    let cancelled = false;
    (async () => {
      try {
        if (isFirebaseEnabled()) {
          // Re-fetch the current user fresh so a stale cached name/data can never
          // be what the app first paints.
          await refreshProfile();
        }
      } catch { /* keep cached profile */ }
      if (cancelled) return;
      authResolvedRef.current = true;
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // run once on mount for the cached-session case
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Telegram auto-login flow (existing) ──────────────────────────
  useEffect(() => {
    if (!tgReady) return;

    const authenticate = async () => {
      // Skip if already authenticated via another method
      if (isAuthenticated && authMethod && authMethod !== 'telegram' && authMethod !== 'dev') {
        authResolvedRef.current = true;
        setLoading(false);
        return;
      }

      setError(null);

      try {
        if (isTelegram && tgUser?.telegramId) {
          const { data: authData, error: authError } = await supabase.auth.signInAnonymously({
            options: {
              data: {
                telegram_id: tgUser.telegramId,
                username: tgUser.username,
              },
            },
          });

          if (authError) throw authError;
          setSession(authData.session);
          const tgProfile = await fetchProfile(tgUser.telegramId);
          setAuthMethod('telegram');
          setIsAuthenticated(true);
          identifyUser(tgUser.telegramId, { method: 'telegram', username: tgUser.username });
        } else if (!isAuthenticated) {
          // Non-Telegram: show login screen (don't auto-authenticate)
          // User must login via email/phone/Google/Apple
          console.log('[OMNISIO] Not in Telegram - login required');
        }
      } catch (err) {
        console.error('[OMNISIO] Auth error:', err);
        setError(err.message);
      } finally {
        authResolvedRef.current = true;
        setLoading(false);
      }
    };

    authenticate();
  }, [tgReady, isTelegram, tgUser, fetchProfile, isAuthenticated, authMethod]);

  // Listen for auth state changes (handles OAuth redirects)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession);

        if (event === 'SIGNED_IN' && newSession?.user && !isAuthenticated) {
          // Handle OAuth redirect completion
          const provider = newSession.user.app_metadata?.provider;
          if (provider === 'google' || provider === 'apple') {
            const { data: prof } = await supabase
              .from('profiles')
              .select('*')
              .eq('auth_id', newSession.user.id)
              .single();

            const userProfile = prof || {
              ...DEFAULT_PROFILE,
              auth_id: newSession.user.id,
              email: newSession.user.email,
              first_name: newSession.user.user_metadata?.full_name || '',
              photo_url: newSession.user.user_metadata?.avatar_url || null,
            };

            setProfile(userProfile);
            setAuthMethod(provider);
            setIsAuthenticated(true);
          }
        }

        if (event === 'SIGNED_OUT') {
          setProfile(null);
          setIsAuthenticated(false);
          setAuthMethod(null);
          clearAuthState();
        }
      }
    );
    return () => subscription.unsubscribe();
  }, [isAuthenticated]);

  // ─── Ensure every profile has a public_id ─────────────────────────
  // Runs whenever profile or authMethod changes. If the loaded/created
  // profile is missing public_id (e.g. migrated user, pre-feature account),
  // generates one and persists it to Supabase. Falls back to localStorage
  // (PENDING_PUBLIC_ID_KEY) if the network write fails.
  useEffect(() => {
    if (!profile || !isAuthenticated) return;
    if (profile.public_id && isValidPublicId(profile.public_id)) return;

    // Call ensurePublicId — it needs a reference to updateProfile which is
    // stable (memoized). We capture authMethod from closure here.
    ensurePublicId(profile, authMethod, updateProfile).then((updatedProfile) => {
      if (updatedProfile && updatedProfile.public_id !== profile.public_id) {
        // ensurePublicId already calls setProfile via updateProfile;
        // if it returned a local-only object (offline case), sync it.
        if (!updatedProfile.id) return; // purely local object, skip
        setProfile((prev) => ({ ...prev, public_id: updatedProfile.public_id }));
      }
    }).catch((err) => {
      console.error('[OMNISIO] ensurePublicId failed:', err);
    });
  }, [profile?.id, profile?.telegram_id, profile?.public_id, isAuthenticated, authMethod, updateProfile]);

  const value = {
    // Auth state
    session,
    isAuthenticated,
    authMethod,
    loading,
    error,

    // Profile data (tokens, level, xp, totem, streak, workouts)
    profile,
    tokens: profile?.tokens || 0,
    // Stable per-user identity (#13: used to scope the onboarding flag so a new
    // user sees onboarding and a returning user skips it).
    uid: deriveUid(profile),

    // Auth actions
    login,
    logout,
    deleteAccount,

    // Profile actions
    updateProfile,
    refreshProfile,
    addTokens,

    // Cross-app: Firebase-forward-compatible token access
    // idToken: quick access (Supabase access_token now; Firebase idToken after Firebase wave)
    idToken: IS_MOCK_MODE ? MOCK_ID_TOKEN : (session?.access_token || null),
    getIdToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export default AuthContext;
